'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { useCurrency } from '@/context/CurrencyContext';

interface Listing {
  id: number;
  title: string;
  price: string;
  plotSize: string;
  builtUpArea: string;
  dealScore: number;
  image: string;
  alt: string;
  url: string;
  label?: string;
  whatsappUrl: string;
}

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

function getDealScoreColor(score: number): string {
  if (score >= 90) return 'bg-[#017743]';
  if (score >= 80) return 'bg-[#2e7d32]';
  return 'bg-[#388e3c]';
}

export default function ListingsSection() {
  const { formatPrice } = useCurrency();
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPage, setNextPage] = useState(2); // Page 2 starts after initial 6 (Page 1)
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const res = await fetch('/api/properties?limit=6&page=1');
        if (res.ok) {
          const result = await res.json();
          const initialListings = result.data || [];
          setProperties(initialListings);
          if (
            initialListings.length >= Math.min(36, result.pagination.total) ||
            initialListings.length < 6
          ) {
            setHasMore(false);
          }
        }
      } catch (err) {
        console.error('Failed to fetch initial listings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  const handleLoadMore = async () => {
    if (loadingMore) return;

    if (properties.length >= 36) {
      setHasMore(false);
      return;
    }

    setLoadingMore(true);
    try {
      const res = await fetch(`/api/properties?limit=6&page=${nextPage}`);
      if (res.ok) {
        const result = await res.json();
        const newProperties = result.data || [];
        if (newProperties.length > 0) {
          const updatedList = [...properties, ...newProperties];
          setProperties(updatedList);
          setNextPage((prev) => prev + 1);

          if (
            updatedList.length >= Math.min(36, result.pagination.total) ||
            newProperties.length < 6
          ) {
            setHasMore(false);
          }
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Failed to load more properties:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="bg-[#f8faf9] py-12">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2
            className="mb-2"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '30px',
              fontWeight: 300,
              color: 'rgb(80, 81, 79)',
              lineHeight: '38px',
            }}
          >
            Shop Our Selection Of First-Rate Plots
          </h2>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="min-h-[400px] flex flex-col items-center justify-center space-y-4">
            <Loader2 size={48} className="animate-spin text-[#017743]" />
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Loading Plot Selection...
            </p>
          </div>
        ) : properties.length === 0 ? (
          <div className="min-h-[400px] flex flex-col items-center justify-center space-y-2 text-slate-400">
            <p className="text-sm font-semibold uppercase tracking-wider">No plots found</p>
          </div>
        ) : (
          <>
            {/* Listings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((listing) => {
                let dealScore = listing.pl_score_total;
                if (dealScore === undefined || dealScore === null || dealScore === 0) {
                  const lowerTitle = (listing.title || '').toLowerCase();
                  if (lowerTitle.includes('meydan')) {
                    dealScore = 60;
                  } else if (lowerTitle.includes('hotel plot')) {
                    dealScore = 90;
                  } else if (lowerTitle.includes('villa plots')) {
                    dealScore = 85;
                  } else if (lowerTitle.includes('jebel ali hills')) {
                    dealScore = 90;
                  } else if (lowerTitle.includes('developer plot')) {
                    dealScore = 80 + (listing.id % 11);
                  } else {
                    dealScore = 80 + (listing.id % 16);
                  }
                }
                dealScore = Math.max(60, Math.min(100, Number(dealScore)));
                const plotSize = listing.land_area || listing.area_size || 'N/A';
                const builtUpArea = listing.area_size || 'N/A';
                const imageSrc = listing.featured_image || '/uploads/placeholder.jpg';
                const formattedPrice = formatPrice(listing.price);
                const propertyUrl = `/property/${listing.slug || listing.id}`;
                const whatsappUrl = `https://api.whatsapp.com/send?phone=+971504331896&text=Hello, I am interested in [${encodeURIComponent(listing.title)}]`;
                const isSold =
                  (listing.status as string)
                    ?.split(',')
                    .some((s: string) => s.trim().toLowerCase() === 'sold') || false;

                return (
                  <div
                    key={listing.id}
                    className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col group"
                  >
                    {/* Image */}
                    <div className="relative h-[200px] overflow-hidden">
                      <a href={propertyUrl}>
                        <AppImage
                          src={imageSrc}
                          alt={listing.title}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      </a>

                      {/* SVG Distressed Stamp Filter definition (hidden but scoped) */}
                      <svg className="absolute w-0 h-0" width="0" height="0">
                        <defs>
                          <filter id="stamp-grunge">
                            <feTurbulence
                              type="fractalNoise"
                              baseFrequency="0.7"
                              numOctaves="5"
                              result="noise"
                            />
                            <feDisplacementMap
                              in="SourceGraphic"
                              in2="noise"
                              scale="5.5"
                              xChannelSelector="R"
                              yChannelSelector="G"
                            />
                          </filter>
                        </defs>
                      </svg>

                      {/* Distressed "Sold" Seal stamp overlay */}
                      {isSold && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/15 z-10 backdrop-blur-[0.5px] pointer-events-none select-none">
                          <div
                            className="relative flex items-center justify-center w-48 h-24 border-[6px] border-double border-white flex-shrink-0 rotate-[-12deg] bg-black/40 backdrop-blur-[0.5px] shadow-[0_0_30px_rgba(255,255,255,0.6),inset_0_0_30px_rgba(255,255,255,0.4)]"
                            style={{ filter: 'url(#stamp-grunge)' }}
                          >
                            <div className="w-full h-full border-[3px] border-white flex items-center justify-center p-4">
                              <span className="text-5xl font-black tracking-widest text-white leading-none animate-pulse">
                                SOLD
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Dynamic Plot Score Circle Progress Gauge & Badge (styled like Featured) */}
                      <div className="absolute top-2 right-2 flex flex-col items-center z-10">
                        {/* Round gauge - Always visible with micro-interaction on card hover */}
                        <div className="relative w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg border border-slate-100/50 transition-transform duration-300 group-hover:scale-105">
                          <svg className="w-full h-full transform -rotate-90">
                            <circle
                              cx="24"
                              cy="24"
                              r="18"
                              className="stroke-slate-100"
                              strokeWidth="4"
                              fill="transparent"
                            />
                            <circle
                              cx="24"
                              cy="24"
                              r="18"
                              className="stroke-[#006838]"
                              strokeWidth="4"
                              fill="transparent"
                              strokeDasharray={113.1}
                              strokeDashoffset={113.1 - (dealScore / 100) * 113.1}
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="absolute text-[13px] font-black text-[#006838] tracking-tight leading-none">
                            {dealScore}
                          </span>
                        </div>

                        {/* Text Badge - Only visible on hover with a premium fade and slide transition with reduced padding */}
                        <div className="bg-[#006838] text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] shadow-md mt-1 whitespace-nowrap opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 pointer-events-none group-hover:pointer-events-auto">
                          Deal Score
                        </div>
                      </div>

                      {/* Action icons - Moved to bottom-right of the image and kept always visible with dynamic lift */}
                      <div className="absolute bottom-2 right-2 flex gap-1.5 z-10">
                        <button
                          className="w-7 h-7 bg-white/95 rounded flex items-center justify-center hover:bg-white transition-all shadow-[0_2px_8px_rgba(0,0,0,0.12)] hover:scale-105 text-gray-600 hover:text-[#006838]"
                          title="View Gallery"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                            />
                          </svg>
                        </button>
                        <button
                          className="w-7 h-7 bg-white/95 rounded flex items-center justify-center hover:bg-white transition-all shadow-[0_2px_8px_rgba(0,0,0,0.12)] hover:scale-105 text-gray-600 hover:text-[#006838]"
                          title="Save Favorite"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                            />
                          </svg>
                        </button>
                        <button
                          className="w-7 h-7 bg-white/95 rounded flex items-center justify-center hover:bg-white transition-all shadow-[0_2px_8px_rgba(0,0,0,0.12)] hover:scale-105 text-gray-600 hover:text-[#006838]"
                          title="Compare"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      </div>
                      {/* Label - reduced padding */}
                      {listing.price_label && (
                        <div className="absolute top-2 left-2 z-10">
                          <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded block shadow-sm">
                            {listing.price_label}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col justify-between">
                      <div>
                        {/* Price */}
                        <div className="text-[14px] font-bold text-[#1a1a1a] mb-1">
                          {formattedPrice}
                        </div>

                        {/* Title */}
                        <h3 className="text-[14px] font-semibold text-[#017743] mb-3 line-clamp-2 leading-snug">
                          <a href={propertyUrl} className="hover:underline">
                            {listing.title}
                          </a>
                        </h3>

                        {/* Sizes */}
                        <div className="flex gap-4 text-[12px] text-[#50514f] mb-4">
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                              />
                            </svg>
                            {plotSize} {listing.area_unit || 'sq. ft'}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            {builtUpArea} {listing.area_unit || 'sq. ft'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 border-t border-slate-50 pt-3">
                        <a
                          href="tel:+971504331896"
                          className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 rounded px-2 py-1.5 text-[12px] text-[#50514f] hover:bg-gray-50 transition-colors"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          Call
                        </a>
                        <a
                          href="mailto:contact@Plotae.ae"
                          className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 rounded px-2 py-1.5 text-[12px] text-[#50514f] hover:bg-gray-50 transition-colors"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          Email
                        </a>
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 rounded px-2 py-1.5 text-[12px] text-[#50514f] hover:bg-gray-50 transition-colors"
                        >
                          <svg
                            className="w-3.5 h-3.5 text-[#25D366]"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-10">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="border border-gray-300 text-[#50514f] rounded-full px-8 py-3 text-[14px] font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 size={16} className="animate-spin text-slate-500" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
